import { useEffect, useRef, useState } from 'react';

import { MultiSelect, MultiSelectType } from 'components/shared/form/InfiniteScrollDropdown';
import { useDebounce } from 'hooks/useDebounce';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import { FilterOption } from 'stores/types';

export type FilterMultiSelectProps = Partial<MultiSelectType<FilterOption>> & {
  name: string;
  options?: FilterOption[];
  debounceTime?: number;
  getOptions?: {
    hook: any;
    params?: object;
    optionsToPrepend?: FilterOption[];
  };
};

const FilterMultiSelect = (props: FilterMultiSelectProps) => {
  const {
    name,
    options,
    getOptions,
    getOptionLabel,
    getOptionValue,
    placeholder,
    value,
    onChange,
    debounceTime = 0,
  } = props;

  const asyncOptions = options ? { options } : getOptions?.hook(getOptions?.params);

  const [selectedOptions, setSelectedOptions] = useState<FilterOption[]>(value ?? []);
  const prevValueRef = useRef(value);
  const isUserChangeRef = useRef(false);

  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      // Don't clobber a pick that is mid-commit. When the user makes rapid consecutive picks, the
      // `value` arriving here is a stale echo of an EARLIER pick's committed onChange, while local
      // `selectedOptions` already holds the newer pick — resetting to `value` would silently drop it
      // (OLIO-3471 QA: the bar quick filter lost clicks made before the list finished reloading).
      // Only sync from `value` when the change is genuinely external (Clear Filters, initial value),
      // signalled by there being no user change in flight.
      if (!isUserChangeRef.current) {
        setSelectedOptions(value ?? []);
      }
    }
  }, [value]);

  const debouncedSelectedOptions = useDebounce(selectedOptions, debounceTime);

  useEffect(() => {
    if (isUserChangeRef.current) {
      onChange?.(new FilterValueArray(debouncedSelectedOptions));
      // Cleared here, once the pick is committed, rather than in the value-sync effect above — so a
      // later pick that lands before this commit's echo returns keeps its in-flight flag.
      isUserChangeRef.current = false;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSelectedOptions]);

  const handleDropdownChange = (changes: FilterValueArray) => {
    isUserChangeRef.current = true;
    setSelectedOptions(changes);
  };

  return (
    <div data-cy={`${name}Filter`}>
      <MultiSelect
        {...asyncOptions}
        getOptionValue={getOptionValue}
        getOptionLabel={getOptionLabel}
        placeholder={placeholder}
        onChange={handleDropdownChange}
        value={selectedOptions}
        prependedOptions={getOptions?.optionsToPrepend || []}
      />
    </div>
  );
};

export default FilterMultiSelect;
